import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import { useBranding } from "../../hooks/useBranding";
import { buildTenantRelativePath } from "../../utils/tenantPreview";
import Badge from "../shared/Badge";
import styles from "./SubNavbar.module.css";
import {
  SUPPORTED_LANGUAGES,
  useLanguage,
} from "../../context/LanguageContext";

/* ── SVG Icons ──────────────────────────────────────────────────────── */
const IconUsers = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
  </svg>
);
const IconEnvelope = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
  </svg>
);
const IconGlobe = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.91 4.33 3.56zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2 0 .68.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.9-4.33-3.56zm2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2 0-.68.07-1.35.16-2h4.68c.09.65.16 1.32.16 2 0 .68-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95c-.96 1.65-2.49 2.93-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2 0-.68-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z" />
  </svg>
);
const IconSearch = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zM9.5 14C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
  </svg>
);

/**
 * Sub-navigation bar — sits below the Topbar.
 *
 * @param {object}   props
 * @param {object}   props.user             - Current user
 * @param {number}   [props.friendRequests] - Pending friend requests count
 * @param {number}   [props.messageCount]   - Unread messages count
 * @param {number}   [props.notifCount]     - Unseen notifications count
 * @param {function} [props.onOpenBox]      - Callback (type: 'friends'|'messages'|'notifications') => void
 * @param {React.ReactNode} [props.friendsDropdown]  - Content for friend requests dropdown
 * @param {React.ReactNode} [props.messagesDropdown]  - Content for messages dropdown
 * @param {React.ReactNode} [props.notificationsDropdown] - Content for notifications dropdown
 */
export default function SubNavbar({
  user,
  friendRequests = 0,
  messageCount = 0,
  notifCount = 0,
  onOpenBox,
  friendsDropdown,
  messagesDropdown,
  notificationsDropdown,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logoUrl, siteName } = useBranding();
  const { language, setLanguage, t } = useLanguage();
  const tenantPath = useCallback((path) => buildTenantRelativePath(path), []);

  // ── Search state ─────────────────────────────────────────────────
  const [searchQ, setSearchQ] = useState("");

  useEffect(() => {
    if (!location.pathname.startsWith("/search")) {
      setSearchQ("");
      return;
    }
    const params = new URLSearchParams(location.search);
    setSearchQ(params.get("q") || "");
  }, [location.pathname, location.search]);

  const submitSearch = (e) => {
    e.preventDefault();
    const q = searchQ.trim();
    if (!q) return;
    navigate(tenantPath(`/search?q=${encodeURIComponent(q)}`));
  };

  // ── Dropdown panels ──────────────────────────────────────────────
  const [activeBox, setActiveBox] = useState(null); // 'friends'|'messages'|'notifications'|null
  const boxRef = useRef(null);
  useOnClickOutside(boxRef, !!activeBox, () => setActiveBox(null));

  useEffect(() => {
    setActiveBox(null);
  }, [location.pathname, location.search]);

  const toggleBox = useCallback(
    (type) => {
      setActiveBox((prev) => {
        const next = prev === type ? null : type;
        if (next && onOpenBox) onOpenBox(next);
        return next;
      });
    },
    [onOpenBox]
  );

  const dropdownContent =
    activeBox === "friends"
      ? friendsDropdown
      : activeBox === "messages"
      ? messagesDropdown
      : activeBox === "notifications"
      ? notificationsDropdown
      : null;

  return (
    <div className={styles.subnavbar}>
      <div className={styles.inner}>
        {/* ── Left: logo ────────────────────────────────────────────── */}
        <Link to={tenantPath("/home")} className={styles.logo} aria-label="Home">
          {logoUrl && (
            <img
              src={logoUrl}
              alt={siteName || "Uniflo"}
              className={styles.logoImg}
            />
          )}
        </Link>

        {/* ── Center: search ────────────────────────────────────────── */}
        <form className={styles.searchForm} onSubmit={submitSearch} autoComplete="off" role="search">
          <span className={styles.searchIcon} aria-hidden="true">
            <IconSearch />
          </span>
          <input
            type="text"
            name="q"
            className={styles.searchInput}
            placeholder={t("subnav_search_placeholder", "Search")}
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            aria-label="Search"
          />
        </form>

        {/* ── Right: notification icons + language ──────────────────── */}
        <div className={styles.rightSection} ref={boxRef}>
          {/* Friend requests */}
          <button
            className={`${styles.iconBtn} ${activeBox === "friends" ? styles.iconBtnActive : ""}`.trim()}
            onClick={() => toggleBox("friends")}
            aria-label={t("subnav_friend_requests", "Friend Requests")}
            aria-haspopup="true"
            aria-expanded={activeBox === "friends"}
          >
            <IconUsers />
            {friendRequests > 0 && (
              <Badge variant="count" count={friendRequests} className={styles.badge} />
            )}
          </button>

          {/* Messages */}
          <button
            className={`${styles.iconBtn} ${activeBox === "messages" ? styles.iconBtnActive : ""}`.trim()}
            onClick={() => toggleBox("messages")}
            aria-label={t("subnav_messages", "Messages")}
            aria-haspopup="true"
            aria-expanded={activeBox === "messages"}
          >
            <IconEnvelope />
            {messageCount > 0 && (
              <Badge variant="count" count={messageCount} className={styles.badge} />
            )}
          </button>

          {/* Notifications */}
          <button
            className={`${styles.iconBtn} ${activeBox === "notifications" ? styles.iconBtnActive : ""}`.trim()}
            onClick={() => toggleBox("notifications")}
            aria-label={t("subnav_notifications", "Notifications")}
            aria-haspopup="true"
            aria-expanded={activeBox === "notifications"}
          >
            <IconGlobe />
            {notifCount > 0 && (
              <Badge variant="count" count={notifCount} className={styles.badge} />
            )}
          </button>

          <label
            className={styles.iconBtn}
            style={{ padding: "0 10px", gap: 8 }}
            aria-label={t("subnav_language", "Language")}
          >
            <IconGlobe />
            <select
              value={language}
              onChange={(event) => setLanguage(event.target.value)}
              style={{
                border: "none",
                background: "transparent",
                color: "inherit",
                fontSize: 12,
                cursor: "pointer",
                outline: "none",
              }}
            >
              {SUPPORTED_LANGUAGES.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>

          {/* ── Dropdown panel ────────────────────────────────────── */}
          {activeBox && (
            <div className={styles.dropdown}>
              <div className={styles.dropdownHeader}>
                <span className={styles.dropdownTitle}>
                  {activeBox === "friends"
                    ? t("subnav_friend_requests", "Friend Requests")
                    : activeBox === "messages"
                    ? t("subnav_messages", "Messages")
                    : t("subnav_notifications", "Notifications")}
                </span>
              </div>
              <div className={styles.dropdownBody}>
                {dropdownContent || (
                  <div className={styles.emptyState}>
                    No {activeBox === "friends" ? "friend requests" : activeBox}
                  </div>
                )}
              </div>
              <div className={styles.dropdownFooter}>
                <Link
                  to={
                    activeBox === "friends"
                      ? "/friends/requests"
                      : activeBox === "messages"
                      ? "/messages/all"
                      : "/notifications/all"
                  }
                  className={styles.seeAllLink}
                  onClick={() => setActiveBox(null)}
                >
                  {t("subnav_see_all", "See all")}
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
